import * as Apollo from '@apollo/client';
import * as Contacts from 'expo-contacts';
import { TFunction } from 'i18next';
import { Alert } from 'react-native';

import { ContactType } from '@components/Invite/Contact';
import {
  Exact,
  UploadContactAttributes,
  UploadContactsMutation,
} from '@graphql/generated';
import { SecurityStorage } from '@utils/storage';

const getDisplayName = ({
  firstName,
  lastName,
  company,
}: Pick<ContactType, 'firstName' | 'lastName' | 'company'>): string => {
  let fullName = [firstName, lastName].filter((item) => item).join(' ');

  if ((!fullName || fullName === '') && company) fullName = company;

  if (!fullName || fullName === '') fullName = '';

  return fullName;
};

const getInitials = ({
  firstName,
  lastName,
  company,
}: Pick<ContactType, 'firstName' | 'lastName' | 'company'>): string => {
  return !firstName && !lastName && !!company
    ? company.charAt(0)
    : (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '');
};

export const isContactsPermissionAllowed = async (): Promise<boolean> => {
  const { status } = await Contacts.getPermissionsAsync();
  return status === 'granted';
};

/**
 * Everything the contact-picker UI renders (name, avatar, email, numbers).
 */
export const CONTACT_UI_FIELDS: Contacts.FieldType[] = [
  Contacts.Fields.Emails,
  Contacts.Fields.PhoneNumbers,
  Contacts.Fields.Image,
];

/**
 * TA-10870 — the background contact sync only ever sends phone numbers to
 * uploadContacts, but used to pull CONTACT_UI_FIELDS anyway. Asking
 * expo-contacts for Image makes Android read every contact's photo blob out
 * of the ContactsContract provider; on a large address book that is a long,
 * allocation-heavy native call, and it runs while the app is backgrounded.
 * Fetch only what the mutation actually consumes.
 */
export const CONTACT_SYNC_FIELDS: Contacts.FieldType[] = [
  Contacts.Fields.PhoneNumbers,
];

export const getDeviceContacts = async (
  options: {
    requestPermission?: boolean;
    fields?: Contacts.FieldType[];
  } = { requestPermission: false }
): Promise<ContactType[]> => {
  const { requestPermission, fields = CONTACT_UI_FIELDS } = options;

  let requestedStatus: Contacts.PermissionStatus | undefined = undefined;

  const { status, canAskAgain } = await Contacts.getPermissionsAsync();

  const hasPermission = status === 'granted';

  if (!hasPermission && canAskAgain && requestPermission) {
    try {
      const permissionResult = await Contacts.requestPermissionsAsync();
      requestedStatus = permissionResult.status;
      console.log('Contact permission request result:', permissionResult);
    } catch (error) {
      console.error('Failed to request contact permissions:', error);
      return [];
    }
  }

  const requestGranted = requestedStatus === 'granted';

  if (!(hasPermission || requestGranted)) {
    console.log('Contact permissions not granted:', {
      hasPermission,
      requestGranted,
      status,
      canAskAgain,
    });
    return [];
  }

  try {
    const { data } = await Contacts.getContactsAsync({ fields });

    const contactsWithPhones = data.filter(
      ({ phoneNumbers }) => !!phoneNumbers && phoneNumbers?.length > 0
    ) as ((typeof data)[0] &
      Required<Pick<(typeof data)[0], 'phoneNumbers'>>)[];

    const contacts: ContactType[] = contactsWithPhones
      .map((item) => {
        const displayName = getDisplayName(item);
        const initials = getInitials(item);

        const { firstName, lastName, image, id, phoneNumbers, company } = item;

        const phoneNumber =
          phoneNumbers?.find((phone) => {
            if (phone?.label?.toLowerCase() === 'mobile') {
              return phone;
            }
          }) || phoneNumbers[0];

        return {
          firstName: firstName || '',
          lastName: lastName || '',
          company: company || '',
          initials,
          displayName,
          phoneNumber: phoneNumber?.number || '',
          allPhoneNumbers: phoneNumbers,
          avatarUrl: image?.uri,
          isPhoneContact: true,
          id: id || '',
          users: [],
          type: 'contact',
          memberCount: 0,
          avatar: null,
        };
      })
      .filter((item) => item !== null);

    console.log(`Retrieved ${contacts.length} contacts from device`);
    return contacts;
  } catch (error) {
    console.error('Failed to retrieve device contacts:', error);
    return [];
  }
};

export const convertDeviceContactsToPhoneNumbers = (
  deviceContacts: ContactType[]
): string[] => {
  // digits field not available on Android
  const numbers = deviceContacts.flatMap((c) =>
    c.allPhoneNumbers?.map((phoneNumber) => phoneNumber.number)
  );
  return numbers.filter((n) => n) as string[];
};

export const getDeviceContactsNew = (
  setShowRequestContactsPermission?: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setDeviceContacts?: React.Dispatch<React.SetStateAction<ContactType[]>>,
  contactsSyncedAt?: boolean,
  uploadContacts?: (
    options?:
      | Apollo.MutationFunctionOptions<
          UploadContactsMutation,
          Exact<{
            attributes: UploadContactAttributes;
          }>,
          Apollo.DefaultContext
        >
      | undefined
  ) => void
) => {
  getDeviceContacts({ requestPermission: true })
    .then(async (d) => {
      console.log(`getDeviceContactsNew retrieved ${d.length} contacts`);
      const isAllowed = await isContactsPermissionAllowed();
      setShowRequestContactsPermission?.(!isAllowed);
      setDeviceContacts?.(d);
      if (d.length > 0 && !contactsSyncedAt) {
        const phoneNumbers = convertDeviceContactsToPhoneNumbers(d);
        uploadContacts?.({ variables: { attributes: { phoneNumbers } } });
      }
    })
    .catch((error) => {
      console.error('getDeviceContactsNew failed:', error);
      setShowRequestContactsPermission?.(false);
      setDeviceContacts?.([]);
    });
};

export const getContacts = async (
  setShowRequestContactsPermission?: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setDeviceContacts?: React.Dispatch<React.SetStateAction<ContactType[]>>,
  contactsSyncedAt?: boolean,
  t?: TFunction<'models', undefined>,
  uploadContacts?: (
    options?:
      | Apollo.MutationFunctionOptions<
          UploadContactsMutation,
          Exact<{
            attributes: UploadContactAttributes;
          }>,
          Apollo.DefaultContext
        >
      | undefined
  ) => void
) => {
  try {
    const requestContactPermission = await SecurityStorage.getItem(
      'request_contact_permission'
    );

    if (requestContactPermission === '1') {
      console.log('Contact permission already granted, retrieving contacts');
      getDeviceContactsNew(
        setShowRequestContactsPermission,
        setDeviceContacts,
        contactsSyncedAt,
        uploadContacts
      );
      return;
    }

    if (!t) {
      console.error('Translation function not available for permission dialog');
      return;
    }

    Alert.alert(
      t('models:chat.contactPermissions'),
      t('models:chat.contactPermission'),
      [
        {
          text: t('shared:continue'),
          style: 'destructive',
          onPress: async () => {
            try {
              await SecurityStorage.setItem('request_contact_permission', '1');
              console.log('Contact permission granted by user');
              getDeviceContactsNew(
                setShowRequestContactsPermission,
                setDeviceContacts,
                contactsSyncedAt,
                uploadContacts
              );
            } catch (error) {
              console.error('Failed to save contact permission:', error);
            }
          },
        },
      ]
    );
  } catch (error) {
    console.error('Failed to check contact permission from storage:', error);
  }
};

export const useDeviceContacts = () => {
  return {
    getDeviceContacts,
    convertDeviceContactsToPhoneNumbers,
    isContactsPermissionAllowed,
    getDeviceContactsNew,
    getContacts,
  };
};
